import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useOnboarding } from '@/shared/hooks/useInteractions';
import { BreaksGalleryPage } from '@/features/manager-breaks-demo/components/BreaksGalleryPage';
import { InsightShell } from './InsightShell';
import { WelcomeScreen } from './screens/WelcomeScreen';

/**
 * The onboarding flow: a welcome, then the engineering-breaks gallery as the
 * final step. Advancing past the gallery completes onboarding and routes into
 * the app.
 */
const TOTAL_STEPS = 2;

export function InsightsFlow() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { getToken, sessionClaims } = useAuth();

  const alreadyOnboarded = Boolean(sessionClaims?.metadata.companyId);
  const [stepIndex, setStepIndex] = useState(0);

  const handleFinish = async () => {
    await getToken({ skipCache: true });
    navigate({
      to: '/command-center',
      replace: true,
    });
  };

  const handleFinishError = (error: Error) => {
    toast.error(t('insights.completeFailed'), {
      description: error.message,
    });
  };

  const { mutate: completeOnboarding, isPending: isFinishing } = useOnboarding({
    onSuccess: handleFinish,
    onError: handleFinishError,
  });

  const finish = () => {
    if (alreadyOnboarded) {
      void handleFinish();
      return;
    }
    completeOnboarding({});
  };

  // Step 2: the breaks gallery, rendered full-bleed (it brings its own canvas).
  if (stepIndex === 1) {
    return <BreaksGalleryPage onContinue={finish} isFinishing={isFinishing} />;
  }

  // Step 1: welcome.
  return (
    <InsightShell currentStep={stepIndex + 1} totalSteps={TOTAL_STEPS}>
      <WelcomeScreen onContinue={() => setStepIndex(1)} />
    </InsightShell>
  );
}
